"use client";

import { SectionLayout } from "@/components/layout/SectionLayout";
import { PricingCard } from "@/data/prices";
import { SectionTitlte } from "@/components/sections/SectionTitlte";
import { useState } from "react";
import { AnimatedPrice } from "@/components/AnimatedPrice";

interface AllPricesSectionProps {
  pricingCards: PricingCard[];
}

export function AllPricesSection({ pricingCards }: AllPricesSectionProps) {
  const [toggles, setToggles] = useState<Record<string, boolean>>({});

  const handleToggle = (cardId: string) => {
    setToggles((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  return (
    <SectionLayout className="py-72">
      <div className="flex flex-col items-start gap-6 justify-center w-full">
        <SectionTitlte
          title="Tarifs"
          subtitle="Licence FFT incluse • Accès à tous nos sites • Animations & événements tout au long de l'année"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
          {pricingCards.map((card: PricingCard) => (
            <div
              key={card.id}
              className="bg-primary p-24 pb-36 border border-[#E2E2E2] hover:border-tertiary transition-all duration-300 h-full flex flex-col gap-24"
            >
              <div className="bg-[#E2E2E2] text-accent font-bold text-base px-4 py-2 w-fit">
                {card.title}
              </div>

              <div className="flex items-end gap-2">
                <AnimatedPrice
                  className="text-5xl font-bold text-accent leading-none"
                  value={
                    (toggles[card.id] ?? card.toggleOption?.enabled)
                      ? (card.toggleOption?.toggledAmount ?? card.amount)
                      : card.amount
                  }
                />
                <span className="text-2xl text-accent/75">{card.period}</span>
              </div>
              <div className="flex items-center border-b border-[#E2E2E2]"></div>

              <div className="flex flex-col gap-2">
                {card.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    {!feature.isItalic && (
                      <svg
                        className="w-4 h-4 text-accent mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    <span
                      className={`text-sm text-accent/75 ${
                        feature.isItalic ? "italic" : ""
                      }`}
                    >
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>

              {card.toggleOption && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleToggle(card.id)}
                    className={`w-10 h-6 rounded-full transition-colors ${
                      toggles[card.id] ? "bg-tertiary" : "bg-[#E2E2E2]"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 bg-white rounded-full transition-transform ${
                        toggles[card.id] ? "translate-x-5" : "translate-x-1"
                      }`}
                    />
                  </button>
                  <span className="text-sm text-accent/75">
                    {card.toggleOption.label}
                  </span>
                </div>
              )}

              {card.registerLink && (
                <a
                  href={card.registerLink}
                  target={
                    /^https?:\/\//.test(card.registerLink)
                      ? "_blank"
                      : undefined
                  }
                  rel={
                    /^https?:\/\//.test(card.registerLink)
                      ? "noopener noreferrer"
                      : undefined
                  }
                  className="w-full border border-tertiary text-tertiary bg-primary py-2 px-4 hover:bg-tertiary hover:text-white transition-colors font-medium mt-auto text-center"
                >
                  S'inscrire
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Remise famille en bas de section */}
        <p className="text-sm text-accent/75 mt-6">
          ➜ Tarif famille : -10% dès 2 membres d'un même foyer, -15% dès 3
          membres et + (hors formule Enfant/Parent).
        </p>
      </div>
    </SectionLayout>
  );
}

